/*!
 * Dalafisk – motion
 * ------------------------------------------------------------------
 * Scroll reveal for dalafisk-* sections, ported from the HTML design where
 * sections below the hero fade and rise as they enter the viewport.
 *
 * Loaded with a defer script tag from every section that needs it, so it is
 * guarded against running twice and no layout file has to be edited.
 *
 * Marks [data-dalafisk-reveal] with data-dalafisk-inview once visible. The
 * attribute is never removed, so a section does not re-animate when scrolled
 * past a second time.
 *
 * NOTE: the design's cross-page transition is a separate, page-level concern
 * and is not implemented here yet.
 */
(function () {
  'use strict';

  if (window.__dalafiskMotion) return;
  window.__dalafiskMotion = true;

  // Gate the hidden state on this class. Sections are only invisible once this
  // script is running, so if it never loads, fails to parse, or the browser has
  // no IntersectionObserver, the content is simply visible instead of stuck at
  // opacity 0 forever.
  document.documentElement.classList.add('dalafisk-motion');

  var REVEAL = '[data-dalafisk-reveal]';
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function revealNow(el) {
    el.setAttribute('data-dalafisk-inview', '');
  }

  function revealAll(root) {
    var nodes = (root || document).querySelectorAll(REVEAL);
    for (var i = 0; i < nodes.length; i++) revealNow(nodes[i]);
  }

  // With motion turned off, show everything immediately and never observe.
  if (reduced && reduced.matches) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { revealAll(); });
    } else {
      revealAll();
    }
    return;
  }

  if (!('IntersectionObserver' in window)) {
    revealAll();
    return;
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      revealNow(entry.target);
      observer.unobserve(entry.target);
    });
  }, {
    // Start the reveal slightly before the section reaches the fold so it has
    // finished by the time it is properly in view.
    rootMargin: '0px 0px -10% 0px',
    threshold: 0.01
  });

  function observe(root) {
    var nodes = (root || document).querySelectorAll(REVEAL);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.hasAttribute('data-dalafisk-inview')) continue;
      // Already on screen at load (e.g. the section under the hero): reveal it
      // without waiting for a scroll that may never come.
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        revealNow(el);
      } else {
        observer.observe(el);
      }
    }
  }

  function init() { observe(document); }

  // Backstops. Nothing below should ever be needed, but a section that is
  // hidden by CSS and revealed by JS must not be able to stay hidden: a missed
  // observer callback would silently cost the shop a whole section.
  function sweep() {
    var nodes = document.querySelectorAll(REVEAL + ':not([data-dalafisk-inview])');
    for (var i = 0; i < nodes.length; i++) {
      var rect = nodes[i].getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) revealNow(nodes[i]);
    }
  }
  window.addEventListener('load', sweep);
  setTimeout(sweep, 3000);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor swaps section markup in without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    observe(e.target || document);
  });

  /* ------------------------------------------------------------------
     Parallax — [data-dalafisk-parallax]
     ------------------------------------------------------------------
     The design's about-page hero photo is taller than its frame and drifts
     vertically as the page scrolls. Opt-in per element so any section can use
     it; the CSS already frames the image correctly, so an element that never
     gets moved simply sits still.

     Under prefers-reduced-motion the outer function has already returned long
     before reaching this block, so the photo never moves. The check is
     repeated here so that stays true if the order above ever changes.
     ------------------------------------------------------------------ */
  (function () {
    if (reduced && reduced.matches) return;

    var frames = [];
    var queued = false;

    function move() {
      queued = false;
      for (var i = 0; i < frames.length; i++) {
        var frame = frames[i];
        var img = frame.querySelector('img');
        if (!img) continue;
        var r = frame.getBoundingClientRect();
        // Off screen: nothing to do, and no style write either.
        if (r.bottom < 0 || r.top > window.innerHeight) continue;
        var middle = (r.top + r.height / 2) / window.innerHeight;
        var shift = Math.max(-8, Math.min(8, (0.5 - middle) * 16));
        img.style.transform = 'translate3d(0,' + shift.toFixed(2) + '%,0)';
      }
    }

    function request() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(move);
    }

    function collect(root) {
      var nodes = (root || document).querySelectorAll('[data-dalafisk-parallax]');
      for (var i = 0; i < nodes.length; i++) {
        if (frames.indexOf(nodes[i]) === -1) frames.push(nodes[i]);
      }
      if (frames.length) move();
    }

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    document.addEventListener('shopify:section:load', function (e) {
      collect(e.target || document);
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { collect(); });
    } else {
      collect();
    }
  })();
})();
